import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Calendar, MapPin, Users, CreditCard, Loader2, ExternalLink } from 'lucide-react';
import { Button } from '../components/ui/button';
import { API_URL } from '../utils/api';

/**
 * Embeddable booking widget for external websites
 * Displays event info and booking button in a compact format
 */
export default function EmbedBookingWidget() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [event, setEvent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchEvent();
  }, [slug]);

  const fetchEvent = async () => {
    try {
      const response = await fetch(`${API_URL}/api/weekends/${slug}`);
      if (response.ok) {
        const data = await response.json();
        setEvent(data);
      } else if (response.status === 301) {
        // Handle redirect
        const location = response.headers.get('Location');
        if (location) {
          window.location.href = location.replace('/weekends/', '/embed/weekends/');
        }
      } else {
        setError('Event not found');
      }
    } catch (err) {
      setError('Failed to load event');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-GB', { 
      weekday: 'short',
      day: 'numeric', 
      month: 'short', 
      year: 'numeric' 
    });
  };

  const getLowestPrice = () => {
    if (!event) return null;
    const prices = [];
    
    if (event.room_options) {
      event.room_options.forEach(room => {
        if (room.price_per_person) prices.push(room.price_per_person);
      });
    }
    
    if (event.ticket_options) {
      event.ticket_options.forEach(ticket => {
        if (ticket.price) prices.push(ticket.price);
      });
    }
    
    return prices.length > 0 ? Math.min(...prices) : null;
  };

  const handleBookNow = () => {
    // Open in new tab on parent site
    window.open(`https://linedancecentral.uk/weekends/${slug}`, '_blank');
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px] bg-white">
        <Loader2 className="w-8 h-8 animate-spin text-violet-600" />
      </div>
    );
  }

  if (error || !event) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[400px] bg-white p-6 text-center">
        <p className="text-slate-500 mb-4">{error || 'Event not found'}</p>
        <a 
          href="https://linedancecentral.uk/weekends" 
          target="_blank" 
          rel="noopener noreferrer"
          className="text-violet-600 hover:underline"
        >
          View all events
        </a>
      </div>
    );
  }

  const lowestPrice = getLowestPrice();

  return (
    <div className="bg-white font-sans" style={{ fontFamily: 'system-ui, -apple-system, sans-serif' }}>
      {/* Header with gradient */}
      <div className="bg-gradient-to-br from-violet-600 to-purple-700 p-6 text-white">
        <h2 className="text-xl font-bold mb-2 leading-tight">{event.title}</h2>
        <div className="flex items-center gap-2 text-violet-100 text-sm">
          <Calendar className="w-4 h-4" />
          <span>{formatDate(event.start_date)} - {formatDate(event.end_date)}</span>
        </div>
      </div>

      {/* Event image if available */}
      {event.image_url && (
        <div className="h-32 overflow-hidden">
          <img 
            src={event.image_url} 
            alt={event.title}
            className="w-full h-full object-cover"
          />
        </div>
      )}

      {/* Details */}
      <div className="p-6 space-y-4">
        {/* Venue */}
        <div className="flex items-start gap-3">
          <MapPin className="w-5 h-5 text-violet-500 mt-0.5 shrink-0" />
          <div>
            <p className="font-medium text-slate-900">{event.venue_name}</p>
            <p className="text-sm text-slate-500">{event.venue_address}</p>
          </div>
        </div>

        {/* Price */}
        {lowestPrice && (
          <div className="flex items-center gap-3">
            <CreditCard className="w-5 h-5 text-violet-500 shrink-0" />
            <div>
              <p className="font-medium text-slate-900">From £{lowestPrice.toFixed(2)} per person</p>
              <p className="text-sm text-slate-500">Deposit options available</p>
            </div>
          </div>
        )}

        {/* Room/ticket count */}
        {(event.room_options?.length > 0 || event.ticket_options?.length > 0) && (
          <div className="flex items-center gap-3">
            <Users className="w-5 h-5 text-violet-500 shrink-0" />
            <p className="text-slate-700">
              {event.room_options?.length > 0 && `${event.room_options.length} room options`}
              {event.room_options?.length > 0 && event.ticket_options?.length > 0 && ' • '}
              {event.ticket_options?.length > 0 && `${event.ticket_options.length} ticket options`}
            </p>
          </div>
        )}

        {/* Book Now Button */}
        <Button 
          onClick={handleBookNow}
          className="w-full bg-violet-600 hover:bg-violet-700 text-white py-6 text-lg font-semibold"
        >
          View Details & Book
          <ExternalLink className="w-5 h-5 ml-2" />
        </Button>

        {/* Attribution */}
        <p className="text-center text-xs text-slate-400">
          Powered by{' '}
          <a 
            href="https://linedancecentral.uk" 
            target="_blank" 
            rel="noopener noreferrer"
            className="text-violet-500 hover:underline"
          >
            Line Dance Central
          </a>
        </p>
      </div>
    </div>
  );
}
